import { useState } from 'react';
import type { SongListItem } from '@/types/song';
import { formatSongKey } from '@/utils/songKey';

type Props = {
  songs: SongListItem[];
  selectedId: string | null;
  loading: boolean;
  currentUserId?: string;
  onSelect: (song: SongListItem) => void;
  onEdit: (song: SongListItem) => void;
  onDelete: (id: string) => void;
  onDeletePermanently: (id: string) => void;
};

const SongList = ({
  songs,
  selectedId,
  loading,
  currentUserId,
  onSelect,
  onEdit,
  onDelete,
  onDeletePermanently,
}: Props) => {
  const [menuOpen, setMenuOpen] = useState<string | null>(null);

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="w-6 h-6 rounded-full border-2 border-[#C8A96E]/30 border-t-[#C8A96E] animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto">
      {songs.length === 0 && (
        <div className="flex flex-col items-center justify-center h-40 text-white/20 text-sm">
          <i className="ri-music-2-line text-2xl mb-2"></i>
          Nenhuma música encontrada
        </div>
      )}
      {songs.map((song) => (
        <div
          key={song.id}
          onClick={() => onSelect(song)}
          className={`relative flex items-center justify-between px-5 py-4 border-b border-white/5 cursor-pointer transition-colors group ${
            selectedId === song.id ? 'bg-white/5' : 'hover:bg-white/3'
          }`}
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className={`w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0 ${selectedId === song.id ? 'bg-[#C8A96E]/20' : 'bg-white/5'}`}>
              <i className={`ri-music-2-line text-sm ${selectedId === song.id ? 'text-[#C8A96E]' : 'text-white/30'}`}></i>
            </div>
            <div className="min-w-0">
              <p className="text-white text-sm font-medium truncate">{song.title}</p>
              <p className="text-white/30 text-xs truncate">
                {song.artist}{song.usage_count !== undefined ? ` · ${song.usage_count} pessoas` : ''}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            {song.key && <span className="text-[#C8A96E] text-xs bg-[#C8A96E]/10 px-2 py-0.5 rounded-full">{formatSongKey(song.key, song.mode)}</span>}
            <div className="relative">
              <button
                onClick={(e) => { e.stopPropagation(); setMenuOpen(menuOpen === song.id ? null : song.id); }}
                className="w-6 h-6 flex items-center justify-center text-white/20 hover:text-white/60 cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <i className="ri-more-2-line text-sm"></i>
              </button>
              {menuOpen === song.id && (
                <div className="absolute right-0 top-8 bg-[#1A1915] border border-white/10 rounded-xl overflow-hidden z-20 w-36">
                  {(song.created_by_id === currentUserId || (song.is_public && song.source_song_id === null)) && (
                    <button
                      onClick={(e) => { e.stopPropagation(); onEdit(song); setMenuOpen(null); }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-white/70 hover:text-white hover:bg-white/5 text-sm cursor-pointer"
                    >
                      <i className="ri-edit-line text-sm"></i>
                      {song.is_public && song.created_by_id !== currentUserId ? 'Criar cópia para editar' : 'Editar'}
                    </button>
                  )}
                  {(song.shared_ministry_ids.length > 1 || (song.is_public && song.source_song_id === null)) && (
                    <button
                      onClick={(e) => { e.stopPropagation(); onDelete(song.id); setMenuOpen(null); }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-red-400 hover:bg-white/5 text-sm cursor-pointer"
                    >
                      <i className="ri-link-unlink text-sm"></i>
                      Remover daqui
                    </button>
                  )}
                  {song.created_by_id === currentUserId && (
                    <button
                      onClick={(e) => { e.stopPropagation(); onDeletePermanently(song.id); setMenuOpen(null); }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-red-400 hover:bg-white/5 text-sm cursor-pointer"
                    >
                      <i className="ri-delete-bin-line text-sm"></i>
                      Excluir permanentemente
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default SongList;
